import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { SponsorDto, SponsorService } from '@millionshow/api-client';
import { ScreenSizeStore } from '../../../../stores/screen-size.store';

@Component({
  selector: 'app-sponsor',
  imports: [CommonModule],
  templateUrl: './sponsor.component.html',
  styleUrls: ['./sponsor.component.css'],
})
export default class SponsorComponent {
  private readonly sponsorService = inject<SponsorService>(SponsorService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly screenSize = inject(ScreenSizeStore);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly desktopTrackRef =
    viewChild<ElementRef<HTMLDivElement>>('desktopTrack');
  private readonly reverseTrackRef =
    viewChild<ElementRef<HTMLDivElement>>('reverseTrack');
  private readonly mobileTrackRef =
    viewChild<ElementRef<HTMLDivElement>>('mobileTrack');
  private measureFrame: number | null = null;
  private readonly desktopSpeedPxPerSecond = 72;
  private readonly mobileSpeedPxPerSecond = 60;
  private readonly reverseSpeedPxPerSecond = 68;

  $sponsors = rxResource({
    stream: () => {
      return this.sponsorService.sponsorControllerFindAllV1();
    },
  });

  sponsors = computed<SponsorDto[]>(() => {
    const data = this.$sponsors.value() ?? [];
    return data
      .filter(sponsor => sponsor.isExposed)
      .sort((a, b) => a.order - b.order);
  });

  sponsorTrack = computed(() => {
    return this.buildTrack(this.sponsors());
  });

  sponsorTrackReversed = computed(() => {
    return this.buildTrack([...this.sponsors()].reverse());
  });

  desktopDuration = signal('20s');
  mobileDuration = signal('18s');
  reverseDuration = signal('22s');

  constructor() {
    effect(() => {
      this.sponsors();
      this.screenSize.isMobileSize();
      this.scheduleMeasure();
    });

    this.destroyRef.onDestroy(() => {
      if (this.isBrowser && this.measureFrame !== null) {
        cancelAnimationFrame(this.measureFrame);
      }
    });
  }

  private buildTrack<T>(items: T[]): T[] {
    return Array.from({ length: 4 }, () => items).flat();
  }

  private scheduleMeasure(): void {
    if (!this.isBrowser || this.measureFrame !== null) {
      return;
    }

    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = null;
      this.measureDurations();
    });
  }

  private measureDurations(): void {
    const mobileTrack = this.mobileTrackRef()?.nativeElement;
    const desktopTrack = this.desktopTrackRef()?.nativeElement;
    const reverseTrack = this.reverseTrackRef()?.nativeElement;

    if (mobileTrack) {
      this.mobileDuration.set(
        this.buildDuration(mobileTrack, this.mobileSpeedPxPerSecond, '18s')
      );
      return;
    }

    if (desktopTrack) {
      this.desktopDuration.set(
        this.buildDuration(desktopTrack, this.desktopSpeedPxPerSecond, '20s')
      );
    }

    if (reverseTrack) {
      this.reverseDuration.set(
        this.buildDuration(reverseTrack, this.reverseSpeedPxPerSecond, '22s')
      );
    }
  }

  private buildDuration(
    track: HTMLDivElement,
    pixelsPerSecond: number,
    fallback: string
  ): string {
    const distance = track.scrollWidth / 2;

    if (!Number.isFinite(distance) || distance <= 0) {
      return fallback;
    }

    return `${Math.max(distance / pixelsPerSecond, 6).toFixed(2)}s`;
  }

  protected onImgError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.visibility = 'hidden';
    this.scheduleMeasure();
  }

  protected onImgLoad(): void {
    this.scheduleMeasure();
  }

  protected onWindowResize(): void {
    this.scheduleMeasure();
  }
}
